import axios, {AxiosResponse} from 'axios'
import {getConfig} from '@salesforce/pwa-kit-runtime/utils/ssr-config'
import {getAppOrigin} from '../utils/urlUtils'
import {getValidAccountMangerToken} from './sfccHelpers'
import {invariant} from './invariant'
import type {Request} from 'express'

interface CommerceAPIConfig {
    proxyPath: string
    parameters: {
        organizationId: string
        siteId: string
    }
}

interface AppConfig {
    commerceAPI: CommerceAPIConfig
}

export type PreferencesResponse = Array<{
    groupId: string
    id: string
    value: string | number | boolean
}>

export const fetchPreferences = async (
    req: Request,
    endpoint: string,
    queryParams = '',
    label = 'custom'
): Promise<PreferencesResponse> => {
    const config = getConfig()
    const {app} = config as {app: AppConfig}
    const {commerceAPI} = app

    const access_token: string | null = await getValidAccountMangerToken()
    invariant(
        access_token,
        `fetchPreferences(${label}): Expected access token to be created but failed`
    )

    const url = `${getAppOrigin(req)}${
        commerceAPI.proxyPath
    }/configuration/preferences/v1/organizations/${
        commerceAPI.parameters.organizationId
    }/${endpoint}${queryParams}`

    try {
        const response: AxiosResponse<{limit: number; data: PreferencesResponse}> = await axios.get(
            url,
            {
                headers: {
                    Authorization: `Bearer ${access_token}`,
                    'content-type': 'application/json'
                },
                timeout: 8_000 // 8s — worker returns page 1 on cold miss (5s max) plus buffer
            }
        )
        invariant(response.status === 200, `Failed to fetch ${label} preferences`)
        return response.data.data
    } catch (e: unknown) {
        // Log the error but return empty array so the page still renders.
        // transformPreferences will fall back to defaultValues for each mapping entry.
        const err = e as {message?: string; response?: {status?: number}}
        console.error(`fetchPreferences(${label}): falling back to defaults`, {
            error: err?.message || e,
            status: err?.response?.status
        })
        return []
    }
}
